import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { parseLocalDate } from '../../../utils/dateUtils';


const DeliverableRow = ({ deliverable, onEdit, onDelete, onUpdateStatus, isMobile }) => {
  const [showResources, setShowResources] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-orange-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const resourceAvatars = (
    <div className="flex -space-x-2">
      {deliverable?.assignedResources?.slice(0, 3)?.map((resource, index) => (
        <div
          key={index}
          className="w-7 h-7 rounded-full border-2 border-card overflow-hidden bg-muted"
          title={resource?.name}
        >
          {resource?.avatar ? (
            <Image
              src={resource?.avatar}
              alt={resource?.avatarAlt}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground text-xs font-medium">
              {resource?.name?.charAt(0)}
            </div>
          )}
        </div>
      ))}
      {deliverable?.assignedResources?.length > 3 && (
        <div className="w-7 h-7 rounded-full border-2 border-card bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
          +{deliverable?.assignedResources?.length - 3}
        </div>
      )}
    </div>
  );

  const detailsSection = (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
          <span className="text-xs text-muted-foreground">
            {parseLocalDate(deliverable?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Icon name="CalendarCheck" size={14} color="var(--color-muted-foreground)" />
          <span className="text-xs text-muted-foreground">
            {parseLocalDate(deliverable?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
          <span className="text-xs text-muted-foreground">
            {deliverable?.estimatedHours}h
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Icon name="AlertCircle" size={14} className={getPriorityColor(deliverable?.priority)} />
          <span className={`text-xs font-medium ${getPriorityColor(deliverable?.priority)}`}>
            {deliverable?.priority?.charAt(0)?.toUpperCase() + deliverable?.priority?.slice(1)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(deliverable?.status)}`}>
            {deliverable?.status?.charAt(0)?.toUpperCase() + deliverable?.status?.slice(1)?.replace('-', ' ')}
          </span>

          {deliverable?.assignedResources?.length > 0 && (
            <button
              onClick={() => setShowResources(!showResources)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {resourceAvatars}
              <Icon name={showResources ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>
          )}
        </div>

        <div className="text-sm text-muted-foreground">
          {deliverable?.actualHours > 0 && (
            <span>{deliverable?.actualHours}h / {deliverable?.estimatedHours}h</span>
          )}
        </div>
      </div>

      {showResources && (
        <div className="mt-4 pt-4 border-t border-border">
          <h5 className="text-sm font-medium text-foreground mb-3">Assigned Resources</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {deliverable?.assignedResources?.map((resource, index) => (
              <div key={index} className="flex items-center gap-3 p-2 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background flex-shrink-0">
                  {resource?.avatar ? (
                    <Image
                      src={resource?.avatar}
                      alt={resource?.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground font-medium">
                      {resource?.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {resource?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {resource?.type} • {resource?.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden transition-shadow hover:shadow-md">
        {/* Tap header — always visible on mobile */}
        <button
          className="w-full text-left p-4"
          onClick={() => setMobileExpanded(prev => !prev)}
          aria-expanded={mobileExpanded}
        >
          <div className="flex items-start gap-3">
            {/* Status checkbox — stop propagation so it doesn't toggle expand */}
            <span
              onClick={(e) => {
                e?.stopPropagation();
                onUpdateStatus(deliverable?.id, deliverable?.status === 'completed' ? 'in-progress' : 'completed');
              }}
              className="flex-shrink-0 mt-0.5"
            >
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                deliverable?.status === 'completed'
                  ? 'bg-green-500 border-green-500' :'border-gray-300 hover:border-blue-500'
              }`}>
                {deliverable?.status === 'completed' && (
                  <Icon name="Check" size={14} color="#FFFFFF" />
                )}
              </div>
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className={`text-sm font-medium leading-snug ${deliverable?.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                  {deliverable?.name}
                </h4>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(deliverable?.status)}`}>
                    {deliverable?.status?.charAt(0)?.toUpperCase() + deliverable?.status?.slice(1)?.replace('-', ' ')}
                  </span>
                  <Icon
                    name={mobileExpanded ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    color="var(--color-muted-foreground)"
                  />
                </div>
              </div>

              {/* Collapsed summary row */}
              {!mobileExpanded && (
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="CalendarCheck" size={12} color="var(--color-muted-foreground)" />
                    {parseLocalDate(deliverable?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                    {deliverable?.estimatedHours}h
                  </span>
                  {deliverable?.assignedResources?.length > 0 && (
                    <span className="flex items-center gap-1">
                      {resourceAvatars}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </button>
        {/* Expanded details */}
        {mobileExpanded && (
          <div className="px-4 pb-4 border-t border-border/50">
            {deliverable?.description && (
              <p className="text-sm text-muted-foreground mt-3 mb-3">
                {deliverable?.description}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => onEdit(deliverable)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                <Icon name="Edit2" size={13} color="var(--color-muted-foreground)" />
                Edit
              </button>
              <button
                onClick={() => onDelete(deliverable?.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
              >
                <Icon name="Trash2" size={13} color="#E74C3C" />
                Delete
              </button>
            </div>

            {detailsSection}
          </div>
        )}
      </div>
    );
  }

  // Desktop layout (unchanged)
  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <button
          onClick={() => onUpdateStatus(deliverable?.id, deliverable?.status === 'completed' ? 'in-progress' : 'completed')}
          className="flex-shrink-0 mt-1"
        >
          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
            deliverable?.status === 'completed' 
              ? 'bg-green-500 border-green-500' :'border-gray-300 hover:border-blue-500'
          }`}>
            {deliverable?.status === 'completed' && (
              <Icon name="Check" size={16} color="#FFFFFF" />
            )}
          </div>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-base md:text-lg font-medium text-foreground mb-1">
                {deliverable?.name}
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {deliverable?.description}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(deliverable)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Edit deliverable"
              >
                <Icon name="Edit2" size={16} color="var(--color-muted-foreground)" />
              </button>
              <button
                onClick={() => onDelete(deliverable?.id)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                aria-label="Delete deliverable"
              >
                <Icon name="Trash2" size={16} color="#E74C3C" />
              </button>
            </div>
          </div>

          {detailsSection}
        </div>
      </div>
    </div>
  );
};

export default DeliverableRow;